import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Banner, Button, Field, Input, useToast } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateSellerMutation } from "../services/useUpdateSellerMutation";

const FIELD_WIDTH = { maxWidth: 360 } as const;

/**
 * The seller's name, email and phone as row fields, with Update beside any
 * extra action the page adds (Reissue invite). Saved → a toast naming the
 * seller; failed → a Banner above the fields with Try again, values kept.
 */
export function SellerEditForm({
  sellerId,
  defaultValues,
  extraActions,
}: {
  sellerId: string;
  defaultValues: { name?: string; email?: string; phone?: string };
  extraActions?: ReactNode;
}) {
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phone, setPhone] = useState(defaultValues.phone ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: updateSeller } = useUpdateSellerMutation();
  const formKey = useMemo(
    () =>
      `${defaultValues.name ?? ""}:${defaultValues.email ?? ""}:${defaultValues.phone ?? ""}`,
    [defaultValues.email, defaultValues.name, defaultValues.phone],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- keep controlled phone input synced when default values load asynchronously
    setPhone(defaultValues.phone ?? "");
  }, [defaultValues.phone]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setPhoneError(null);
    const formData = new FormData(event.currentTarget);
    const normalizedPhone =
      phone.trim().length === 0 ? undefined : (toE164OrNull(phone) ?? undefined);
    if (phone.trim().length > 0 && !normalizedPhone) {
      setPhoneError(t(keys.buyer.trackingValidationPhone));
      return;
    }

    const name = String(formData.get("name") ?? "");
    setIsSubmitting(true);
    try {
      await updateSeller({
        sellerId,
        name,
        email: String(formData.get("email") ?? "").trim() || null,
        phone: normalizedPhone,
      });
    } catch (caught) {
      setError(caught instanceof ApiJsonClientError ? caught.message || "" : "");
      return;
    } finally {
      setIsSubmitting(false);
    }

    toast.show({ message: t("admin.sellerSavedToast", { name: name.trim() || defaultValues.name || "" }) });
  };

  return (
    <form
      key={formKey}
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column", gap: 4 }}
    >
      {error !== null ? (
        <div style={{ marginBottom: 8 }}>
          <Banner
            tone="error"
            title={t("admin.sellerSaveFailedTitle")}
            actions={
              <Button size="sm" type="submit" loading={isSubmitting} loadingLabel={t(keys.shell.retrying)}>
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {[error, t("admin.sellerNothingChanged")].filter(Boolean).join(" ")}
          </Banner>
        </div>
      ) : null}
      <Field layout="row" label={t(keys.common.name)} htmlFor="seller-name">
        <Input
          id="seller-name"
          name="name"
          defaultValue={defaultValues.name}
          placeholder={t(keys.placeholders.name)}
          style={FIELD_WIDTH}
        />
      </Field>
      <Field layout="row" label={t(keys.common.email)} htmlFor="seller-email">
        <Input
          id="seller-email"
          name="email"
          type="email"
          dir="ltr"
          defaultValue={defaultValues.email}
          placeholder={t(keys.placeholders.email)}
          style={FIELD_WIDTH}
        />
      </Field>
      <Field
        layout="row"
        label={t(keys.common.phone)}
        htmlFor="seller-phone"
        error={phoneError}
      >
        <Input
          id="seller-phone"
          name="phone"
          type="tel"
          dir="ltr"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
            setPhoneError(null);
          }}
          invalid={Boolean(phoneError)}
          style={FIELD_WIDTH}
        />
      </Field>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 8,
          paddingInlineStart: 152,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="primary"
          size="sm"
          type="submit"
          loading={isSubmitting}
          loadingLabel={t("admin.sellerSaving")}
        >
          {t(keys.common.update)}
        </Button>
        {extraActions ?? null}
      </div>
    </form>
  );
}
